import React from 'react';
import {
  Tooltip,
  IconButton,
  Box,
  Typography,
  useTheme
} from '@mui/material';
import { Info } from '@mui/icons-material';

/**
 * The tooltip presentation, in ONE place.
 *
 * ROADMAP "The rebalance result page reads as a professional report", item 3:
 * one appearance across the platform, three permitted TRIGGERS. Roughly half
 * the things worth explaining cannot carry an icon — an 11px table header, a
 * 3%-wide band in a bar — so the trigger varies and the look never does.
 *
 * Everything below wraps this. A second tooltip component that looked five
 * percent different from every other tooltip is the failure the shared
 * appearance exists to prevent.
 */
const Explained: React.FC<{
  description: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  children: React.ReactElement;
}> = ({ description, placement = 'top', children }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  return (
    <Tooltip
      title={<Box sx={{ p: 1 }}><Typography variant="body2">{description}</Typography></Box>}
      placement={placement}
      arrow
      enterDelay={200}
      leaveDelay={0}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 300,
            backgroundColor: isDarkMode ? '#424242' : 'rgba(0, 0, 0, 0.87)',
            color: isDarkMode ? '#ffffff' : '#ffffff',
            '& .MuiTooltip-arrow': {
              color: isDarkMode ? '#424242' : 'rgba(0, 0, 0, 0.87)',
            },
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
};

/**
 * Trigger (b): the label itself, under a dotted underline.
 *
 * For an inline label or a table header, where a 16px icon beside an 11px cell
 * is noise and thirteen of them are a rash. **The underline is load-bearing** —
 * a tooltip nobody knows is there explains nothing.
 *
 * Focusable, so it opens on keyboard focus and on a tap; hover alone would put
 * every explanation out of reach on a phone.
 */
export const ExplainedText: React.FC<{
  description: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  children: React.ReactNode;
}> = ({ description, placement, children }) => (
  <Explained description={description} placement={placement}>
    <Box
      component="span"
      tabIndex={0}
      sx={{
        borderBottom: '1px dotted',
        borderColor: 'text.secondary',
        cursor: 'help',
        // A dotted rule under a heading must not print as a mystery mark.
        '@media print': { borderBottom: 0 },
      }}
    >
      {children}
    </Box>
  </Explained>
);

/**
 * Trigger (c): the thing itself, with no added affordance.
 *
 * For a graphical element whose own shape IS the target — a bar, a band, a
 * badge. An icon cannot sit inside a 3%-wide band, and an underline under a
 * filled rectangle reads as a border rather than an invitation.
 */
export const ExplainedElement: React.FC<{
  description: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  children: React.ReactElement;
}> = ({ description, placement, children }) => (
  <Explained description={description} placement={placement}>{children}</Explained>
);

interface MetricTooltipProps {
  metric: string;
  description: string;
  learnTopic?: string;
  size?: 'small' | 'medium';
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

const MetricTooltip: React.FC<MetricTooltipProps> = ({
  metric,
  description,
  learnTopic,
  size = 'small',
  placement = 'top'
}) => {
  return (
    <Explained description={description} placement={placement}>
      <IconButton
        size="small"
        sx={{ 
          p: 0,
          ml: 0.5,
          opacity: 0.4,
          color: 'text.secondary',
          '&:hover': {
            opacity: 0.7,
            backgroundColor: 'transparent',
            cursor: 'default',
          },
          cursor: 'default',
          '& .MuiSvgIcon-root': {
            fontSize: '16px',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '50%',
            padding: '2px'
          }
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Info />
      </IconButton>
    </Explained>
  );
};

/* `MetricLabel` used to sit here: exported, named as though it attached a
   tooltip to a label, used by nothing, and its own body said "for now, just
   render children without tooltip functionality". A no-op that looks like a
   feature is worse than an absence — someone reaching for it by name would
   have shipped labels with no explanations and a green test suite. Its job is
   now `ExplainedText` above, which actually does it (ROADMAP item 3). */

export default MetricTooltip;